import React from "react";
import { ChevronDown } from "lucide-react";

// 示例按钮
export const DemoButton = ({ icon: Icon, label, variant = "primary", size = "md", theme, color }) => {
  const sizeClasses = { sm: "px-3 py-1.5 text-xs", md: "px-4 py-2 text-sm", lg: "px-6 py-3 text-base" };
  const bgColor = color || (variant === "primary" ? theme.colors.accentPrimary : variant === "green" ? theme.colors.success : variant === "purple" ? theme.colors.purplePrimary : "var(--color-bg-tertiary)");
  const textColor = variant === "secondary" ? "var(--color-text-primary)" : "white";
  return (
    <span className={`inline-flex items-center gap-2 rounded-lg font-medium ${sizeClasses[size]} pointer-events-none`}
      style={{ backgroundColor: bgColor, color: textColor, opacity: 0.9 }}>
      {Icon && <Icon size={size === "sm" ? 12 : 14} />}{label}
    </span>
  );
};

// 示例卡片
export const DemoCard = ({ title, description, icon: Icon }) => (
  <div className="p-4 rounded-xl" style={{ backgroundColor: "var(--color-bg-card)" }}>
    {Icon && <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
      <Icon size={20} style={{ color: "var(--color-text-secondary)" }} />
    </div>}
    <h4 className="font-bold text-sm mb-1" style={{ color: "var(--color-text-primary)" }}>{title}</h4>
    <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>{description}</p>
  </div>
);

// 示例标签
export const DemoTag = ({ label, color, theme }) => (
  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
    style={{ backgroundColor: `${color || theme.colors.accentPrimary}20`, color: color || theme.colors.accentPrimary }}>
    {label}
  </span>
);

// 提示框
export const TipBox = ({ children, type = "info", theme }) => {
  const colors = { info: theme.colors.info, warning: theme.colors.warning, success: theme.colors.success };
  const icons = { info: "💡", warning: "⚠️", success: "✓" };
  const c = colors[type];
  return (
    <div className="p-3 rounded-lg" style={{ backgroundColor: `${c}15`, border: `1px solid ${c}40` }}>
      <p className="text-xs leading-relaxed" style={{ color: c }}>{icons[type]} {children}</p>
    </div>
  );
};

// 侧边栏导航按钮示例
export const DemoNavItem = ({ icon: Icon, label, active, theme }) => (
  <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border`}
    style={{
      backgroundColor: active ? "var(--color-bg-tertiary)" : "transparent",
      borderColor: active ? "var(--color-border-primary)" : "transparent",
      color: active ? theme.colors.accentPrimary : "var(--color-text-muted)"
    }}>
    <Icon size={18} style={{ color: active ? theme.colors.accentPrimary : "var(--color-text-muted)" }} />
    <span className="text-sm font-medium">{label}</span>
  </div>
);

// 模拟输入框
export const DemoInput = ({ label, placeholder, value }) => (
  <div>
    <label className="block text-xs font-bold uppercase mb-2" style={{ color: "var(--color-text-muted)" }}>{label}</label>
    <div className="px-4 py-2.5 rounded-lg text-sm" style={{ backgroundColor: "var(--color-bg-overlay)", color: value ? "var(--color-text-primary)" : "var(--color-text-muted)" }}>
      {value || placeholder}
    </div>
  </div>
);

// 模拟图片区域
export const DemoImageArea = ({ label, ratio = "21/9", theme, children }) => (
  <div className="rounded-lg border-2 border-dashed flex items-center justify-center" style={{ aspectRatio: ratio, borderColor: "var(--color-border-primary)", backgroundColor: "var(--color-bg-overlay)" }}>
    {children || <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>{label || "图片区域"}</span>}
  </div>
);

// 可折叠章节
export const Section = ({ id, title, icon: Icon, expanded, onToggle, theme, children }) => (
  <div className="rounded-xl border overflow-hidden transition-all"
    style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: expanded ? theme.colors.accentPrimary : "var(--color-border-primary)" }}>
    <button onClick={() => onToggle(id)} className="w-full flex items-center justify-between p-4 transition-colors hover:opacity-80">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
          <Icon size={18} style={{ color: expanded ? "var(--color-text-primary)" : "var(--color-text-muted)" }} />
        </div>
        <h3 className="text-base font-bold" style={{ color: "var(--color-text-primary)" }}>{title}</h3>
      </div>
      <ChevronDown size={20} className={`transition-transform ${expanded ? "rotate-180" : ""}`} style={{ color: "var(--color-text-muted)" }} />
    </button>
    {expanded && <div className="p-5 pt-0 border-t" style={{ borderColor: "var(--color-border-primary)" }}>{children}</div>}
  </div>
);

// 步骤指示器
export const StepIndicator = ({ number, label }) => (
  <div className="flex items-center gap-2">
    <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
      style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-primary)" }}>{number}</div>
    <span className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>{label}</span>
  </div>
);
